/**
 * Individual notification item component
 * Following container/presentation pattern
 */
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@splitfiesta/i18n';
import type { NotificationResponseDto } from '@splitfiesta/shared';

import { RelativeTime } from '@/components/ui/RelativeTime';

import { useMarkNotificationAsReadMutation } from '@/hooks/queries/useNotifications';

import { NotificationService } from '@/services/notification/NotificationService';

interface NotificationItemProps {
  notification: NotificationResponseDto;
  showGroupName?: boolean;
  compact?: boolean; // When true, no padding (for use in pages with custom layout)
}

/**
 * Container component for notification item with click handling
 */
export default function NotificationItem({
  notification,
  showGroupName = false,
  compact = false,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsRead = useMarkNotificationAsReadMutation();

  const handleClick = () => {
    // In the full-page list the row already has an explicit dot
    // toggle for mark-as-read, so the body click is *just* navigation.
    // In the dropdown there's no per-row toggle, so clicking still
    // flips read state as a side-effect.
    if (!compact && notification.id) {
      markAsRead.mutate(notification.id);
    }

    const navigationPath =
      NotificationService.getNotificationPath(notification);
    if (navigationPath) {
      navigate(navigationPath);
    }
  };

  return (
    <NotificationItemPresentation
      notification={notification}
      showGroupName={showGroupName}
      compact={compact}
      onClick={handleClick}
    />
  );
}

/**
 * Presentation component for notification item UI
 */
interface NotificationItemPresentationProps {
  notification: NotificationResponseDto;
  showGroupName?: boolean;
  compact?: boolean;
  onClick: () => void;
}

function NotificationItemPresentation({
  notification,
  showGroupName,
  compact = false,
  onClick,
}: NotificationItemPresentationProps) {
  const { t } = useTranslation('notifications');

  // Get icon based on notification type
  const icon = NotificationService.getNotificationIcon(notification.type);

  // Format the notification message with translation
  const message: string = NotificationService.formatNotificationMessage(
    notification,
    t
  );

  return (
    <button
      onClick={onClick}
      className={`w-full text-left transition-colors ${
        compact
          ? ''
          : `px-4 py-3 ${
              !notification.isRead
                ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
            }`
      }`}
      data-testid={`notification-item-${notification.id}`}
    >
      <div className={`flex gap-3 ${compact ? 'items-start' : ''}`}>
        {/* Type icon — only shown in the dropdown. The full-page list
            uses a Gmail-style layout where the left-side unread dot is
            the only affordance, so the type circle is redundant. */}
        {!compact && (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${NotificationService.getNotificationIconColor(
              notification.type
            )}`}
          >
            {icon}
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 min-w-0 ${compact ? 'pt-1' : ''}`}>
          <p
            className={`text-sm ${
              !notification.isRead
                ? 'font-semibold text-gray-900 dark:text-white'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {message}
          </p>

          {showGroupName && notification.params?.groupName ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {String(notification.params.groupName)}
            </p>
          ) : null}

          {/* Timestamp */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {notification.createdAt ? (
              <RelativeTime date={notification.createdAt} />
            ) : (
              t('common:actions.loading')
            )}
          </p>
        </div>

        {/* Read/Unread indicator - hide in compact mode since page provides its own */}
        {!compact && (
          <div className="shrink-0">
            {!notification.isRead ? (
              <div className="h-2 w-2 rounded-full bg-blue-500" />
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {t('read')}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
